import React, { useState, useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { AppState } from '../store'
import {
  RESTYPE_ADDITION,
  RESTYPE_INTEREST,
  RESTYPE_MONTHS,
  RESTYPE_TARGET,
  reset,
} from '../store/modules/financialCalc'
import { FinancialCalculatorChart } from './FinancialCalculatorChart'
import { CalcForm } from './FinancialCalculatorForms'
import { CalcResultComponent } from './FInancialCalculatorResult'
import { Loading } from './Loading'
import { CalcGuideModal } from './FinancialCalculatorGuideModal'
import { FinancialCalculatorErrorModal } from './FinancialCalculatorErrorModal'
import { Header } from '../components/header'
import { Footer } from '../components/footer'
import '../assets/css/libs.css'
import '../assets/css/style.css'
import { Link } from 'react-router-dom'
import { useHeadTitle } from '../hook/useHeadTitle'
import { useDealerCodeUrl } from '../hook/useDealerCode'
import { useDescription } from '../hook/useDescription'
import { DisclaimerModal } from './DisclaimerModal'
import { useSendEmbedSizeInfo } from '../hook/useSendEmbedHeight'

export interface FormInput {
  target: number
  initial: number
  addition: number
  years: number
  interest: number
  depositInterest: number
}

const tabLabels = [
  { restype: RESTYPE_TARGET, name: '将来の評価額' },
  { restype: RESTYPE_INTEREST, name: '必要な利回り' },
  { restype: RESTYPE_MONTHS, name: '必要な投資期間' },
  { restype: RESTYPE_ADDITION, name: '必要な積立額' },
] as { restype: 'target' | 'interest' | 'months' | 'addition'; name: string }[]

interface TabsProps {
  onClick: (newValue: 'target' | 'interest' | 'months' | 'addition') => void
  onKeyPress: (
    event: React.KeyboardEvent<HTMLLIElement>,
    newValue: 'target' | 'interest' | 'months' | 'addition'
  ) => void
  select: string
}

interface TabPanelProps {
  children: React.ReactNode
  restype: 'target' | 'interest' | 'months' | 'addition'
  select: string
}

const Tabs: React.FC<TabsProps> = (props) => {
  const _listItems = tabLabels.map((tabLabel, index) => (
    <li
      className="c-tab-nav__item"
      key={tabLabel.restype}
      id={`calc-tab-${tabLabel.restype}`}
      aria-controls={`calc-tabpanel-${tabLabel.restype}`}
      role="tab"
      tabIndex={0}
      onClick={() => props.onClick(tabLabel.restype)}
      onKeyPress={(e) => props.onKeyPress(e, tabLabel.restype)}
    >
      {/* eslint-disable jsx-a11y/anchor-is-valid */}
      <a
        href="#"
        className={
          props.select === tabLabel.restype ? 'c-tab-nav__link is-current' : 'c-tab-nav__link'
        }
      >
        {index === 0 ? <span className="u-md-mr1em">{tabLabel.name}</span> : tabLabel.name}
      </a>
      {/* eslint-enable jsx-a11y/anchor-is-valid */}
    </li>
  ))
  return (
    <nav className="c-tab-nav">
      <ul className="c-tab-nav__list" role="tablist">
        {_listItems}
      </ul>
    </nav>
  )
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, restype, select } = props

  return (
    <div
      role="tabpanel"
      hidden={select !== restype}
      id={`calc-tabpanel-${restype}`}
      aria-labelledby={`calc-tab-${restype}`}
    >
      {children}
    </div>
  )
}

export const FinancialCalculator: React.FC = () => {
  const dispatch = useDispatch()
  const { fetchState } = useSelector((state: AppState) => state.financialCalc)
  const [selectTab, setSelectTab] = useState(RESTYPE_TARGET)
  const [isShowGuide, setShowGuide] = useState(false)
  const [isShowDisclaimer, setShowDisclaimer] = useState(false)
  const [inputErrorMessage, setInputErrorMessage] = useState('')

  const handleChange = (newValue: 'target' | 'interest' | 'months' | 'addition') => {
    setSelectTab(newValue)
  }

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLLIElement>,
    newValue: 'target' | 'interest' | 'months' | 'addition'
  ) => {
    if (event.key === 'Enter') handleChange(newValue)
  }

  const resetInputErrorMessage = useCallback(() => setInputErrorMessage(''), [setInputErrorMessage])

  useHeadTitle('今こそ資産運用を考えよう', 'つみたてシミュレーション')
  useDescription(
    'シミュレーションを通じてご自身の資産運用を考えてみませんか。野村アセットマネジメントが資産運用に取り組む皆さまをサポートします。'
  )
  useEffect(() => {
    dispatch(reset())
    return () => {
      dispatch(reset())
    }
  }, [dispatch])

  useSendEmbedSizeInfo()

  return (
    <React.Fragment>
      <Header />
      <body className="include tumitate">
        <main>
          <section>
            <div className="c-title-wrap">
              <h2 className="c-title--primary">つみたてシミュレーション</h2>
              <p className="c-title__lead">
                金額や期間などを決めて色々なパターンの「つみたて」を計算
              </p>
              <p className="u-text--right">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href="#" className="c-title__link-info" onClick={() => setShowGuide(true)}>
                  使い方ガイド
                </a>
              </p>
            </div>
            <div id="app-root" role="application">
              <Tabs onClick={handleChange} onKeyPress={handleKeyPress} select={selectTab} />
              <div className="u-bg--white">
                <div className="l-container">
                  <div className="p-simulation">
                    <TabPanel select={selectTab} restype={RESTYPE_TARGET}>
                      <CalcForm
                        caseType={RESTYPE_TARGET}
                        setInputErrorMessage={setInputErrorMessage}
                        isResponsive={true}
                      >
                        <CalcResultComponent caseType={RESTYPE_TARGET} />
                      </CalcForm>
                    </TabPanel>
                    <TabPanel select={selectTab} restype={RESTYPE_INTEREST}>
                      <CalcForm
                        caseType={RESTYPE_INTEREST}
                        setInputErrorMessage={setInputErrorMessage}
                        isResponsive={true}
                      >
                        <CalcResultComponent caseType={RESTYPE_INTEREST} />
                      </CalcForm>
                    </TabPanel>
                    <TabPanel select={selectTab} restype={RESTYPE_MONTHS}>
                      <CalcForm
                        caseType={RESTYPE_MONTHS}
                        setInputErrorMessage={setInputErrorMessage}
                        isResponsive={true}
                      >
                        <CalcResultComponent caseType={RESTYPE_MONTHS} />
                      </CalcForm>
                    </TabPanel>
                    <TabPanel select={selectTab} restype={RESTYPE_ADDITION}>
                      <CalcForm
                        caseType={RESTYPE_ADDITION}
                        setInputErrorMessage={setInputErrorMessage}
                        isResponsive={true}
                      >
                        <CalcResultComponent caseType={RESTYPE_ADDITION} />
                      </CalcForm>
                    </TabPanel>
                    <Loading apiStatus={fetchState} />
                    <FinancialCalculatorChart select={selectTab} isResponsive={true} />
                    <div className="u-mt50px">
                      <p className="c-note u-mb20px" style={{ color: 'red' }}>
                        【お詫び】2026年6月29日（月）16：30 ～ 7月1日（水）22：10頃の間、当ページにおいて、一時的にシミュレーション結果のグラフの一部が表示されない事象が発生しておりました。現在は復旧しており、通常通りご利用いただける状態となっております。ご利用の皆様にはご迷惑をおかけしましたことを、深くお詫び申し上げます。原因の究明を進めるとともに、再発防止に努めてまいります。
                      </p>
                      <p className="c-note">
                        将来の運用成果を示唆あるいは保証するものではありません。内容の正確性、完全性、適合性等を保証するものではありません。
                        当該情報の利用による投資等の損益はすべてお客さまに帰属し、野村アセットマネジメント株式会社は一切責任を負いません。
                        サービス内容、免責事項、詳細は
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid*/}
                        <a href="#" onClick={() => setShowDisclaimer(true)}>
                          こちら
                        </a>
                        を御覧ください。
                      </p>
                    </div>
                    <div className="c-button-group">
                      <Link
                        to={useDealerCodeUrl('/')}
                        className="c-button--primary c-button-arrow--back"
                      >
                        <span className="c-button__text--xs">TOPページへ戻る</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <CalcGuideModal isShow={isShowGuide} setShow={setShowGuide} />
            <DisclaimerModal isShow={isShowDisclaimer} setShow={setShowDisclaimer} />
            <FinancialCalculatorErrorModal
              inputErrorMessage={inputErrorMessage}
              resetInputErrorMessage={resetInputErrorMessage}
            />
          </section>
          <div id="modal-root"></div>
        </main>
        <Footer />
      </body>
    </React.Fragment>
  )
}
